import { Typography } from '@gto/ui/typography'

type DashboardSlideTitleProps = {
  text: string | string[]
  /**
   * Renders the headline at the 24px/30px the Figma frames specify, instead of
   * the 32px `h4` the carousel has always used.
   */
  compact?: boolean
  /**
   * Authored headline size, overriding both the default and `compact`. An inline
   * style rather than a class: a free number has no Tailwind class to name at
   * build time. Clamped here — see `TITLE_SIZE_MIN_PX`.
   */
  sizePx?: number
}

/**
 * A last-resort clamp, deliberately NOT the contract's copy of these numbers.
 *
 * Announcements reach the client through the user-settings blob with no runtime
 * schema validation, so anything editing the column outside the Ninja schema —
 * Django admin, a data migration, a future endpoint — reaches this component
 * unchecked. Unclamped, one bad row paints a headline tall enough to displace the
 * carousel for every signed-in user.
 *
 * Private on purpose. What the admin form may SAVE is decided by the API contract
 * (`@gto/api-client/admin/zod`), not by what this component happens to survive —
 * exporting these would make a presentational lib the authority on what the
 * backend accepts, and a designer tightening the clamp would silently narrow what
 * operators are allowed to author.
 */
const TITLE_SIZE_MIN_PX = 16
const TITLE_SIZE_MAX_PX = 48

// The brand guidelines set every banner headline in Poppins semibold. The `h4`
// and `h5` variants already carry that weight, so the title adds no weight of
// its own: an override here is what made the carousel headline bold.
//
// `h5` is the design system's 24px heading, but its 120% line-height resolves to
// 28.8px — the frames want 125%, so that one value is set explicitly.
const COMPACT_CLASSES = 'leading-[30px]'

// Pinned so the compact variant stays an `<h4>`: `Typography` otherwise derives the
// element from the variant, which would leave one slide's heading level differing
// from every other slide's purely because its headline is smaller.
const TITLE_ELEMENT = 'h4'

// Unitless, so an authored size scales its own leading instead of inheriting one
// fixed for a different size.
const TITLE_LINE_HEIGHT_RATIO = 1.235

// Renders authored newlines as line breaks without treating the headline as
// markup — the text stays a plain React child.
const TITLE_WRAP_CLASS = 'whitespace-pre-line'

export const DashboardSlideTitle = ({
  text,
  compact = false,
  sizePx,
}: DashboardSlideTitleProps) => {
  // `compact` is not re-expressed as a `sizePx` value: it also swaps to `h5`, which
  // drops `h4`'s tracking, and the one slide using it is signed off at that rendering.
  const useCompact = compact && sizePx === undefined
  const variant = useCompact ? 'h5' : 'h4'
  const className = useCompact ? `${COMPACT_CLASSES} ${TITLE_WRAP_CLASS}` : TITLE_WRAP_CLASS
  const style =
    sizePx === undefined
      ? undefined
      : {
          fontSize: `${Math.min(Math.max(sizePx, TITLE_SIZE_MIN_PX), TITLE_SIZE_MAX_PX)}px`,
          lineHeight: TITLE_LINE_HEIGHT_RATIO,
        }

  if (Array.isArray(text)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {text.map((item, index) => (
          <Typography
            // eslint-disable-next-line @eslint-react/no-array-index-key -- static title lines from fixed slide config, never reordered
            key={index}
            className={className}
            color={index ? 'primary' : 'text.primary'}
            component={TITLE_ELEMENT}
            style={style}
            variant={variant}
          >
            {item}
          </Typography>
        ))}
      </div>
    )
  }

  return (
    <Typography
      className={className}
      color="primary"
      component={TITLE_ELEMENT}
      style={style}
      variant={variant}
    >
      {text}
    </Typography>
  )
}
